import React from "react";
import Navbar from "../../assets/Navbar/Navbar";
import Footer from "../../assets/Footer/Footer";
import BreadcrumbHeader from "../../assets/Breadcrumbs/Breadcrumbs";
import Slideshow from "../../assets/Slideshow/Slideshow";
import "./PersonalProjects.css";

import inventory1 from "../../assets/Images/Inventoryapp/1.png";
import inventory2 from "../../assets/Images/Inventoryapp/2.png";
import inventory3 from "../../assets/Videos/4.mp4";

const InventoryManagementApp = () => {
  // Screenshots / video
  const images = [inventory2, inventory1, inventory3];

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="page-container">
      <Navbar />

      <div className="content-lo1-headings">
        <h1>Inventory Management App</h1>
        <p>
          A personal project I built to solve a real problem in my parents’ restaurant: tracking stock
          properly without guessing.
        </p>
      </div>

      <div className="line"></div>

      <div className="breadcrumb-wrap" style={{ padding: "0.1rem 7.5rem" }}>
        <BreadcrumbHeader />
      </div>

      <div className="content-lo1">
        <div className="pp-layout">
          {/* LEFT: Content */}
          <div className="pp-left">
            {/* Anchors */}
            <div className="pp-anchors">
              <button onClick={() => scrollToId("overview")}>Overview</button>
              <button onClick={() => scrollToId("problem")}>Problem</button>
              <button onClick={() => scrollToId("solution")}>Solution</button>
              <button onClick={() => scrollToId("features")}>Features</button>
              <button onClick={() => scrollToId("learning")}>What I Learned</button>
            </div>

            <section id="overview" className="pp-section">
              <h2>Overview</h2>
              <p>
                I chose this as my <b>Tech Case</b> during Smart Mobile because I wanted to build something
                that could actually help my parents’ restaurant not just a “school app” that ends when
                the semester ends.
              </p>
              <p>
                The idea is simple: if stock is visible and easy to update, daily work becomes smoother.
                It can reduce waste, save time, and make restocking decisions way less stressful.
              </p>
              <p>
                What I’ve already managed to do is connect the app to a database and store items properly
                so the inventory list is real data, not temporary notes.
              </p>
            </section>

            <section id="problem" className="pp-section">
              <h2>Problem</h2>
              <p>
                In my parents’ restaurant, stock tracking was mostly manual. There wasn’t a reliable system,
                so a lot of decisions were based on memory or guessing.
              </p>
              <p>That made it hard to know:</p>
              <ul>
                <li>What items were running low</li>
                <li>Which ingredients were used the most each day</li>
                <li>What should be reordered and when</li>
                <li>What’s missing until it becomes a problem during service</li>
              </ul>
            </section>

            <section id="solution" className="pp-section">
              <h2>Solution</h2>
              <p>
                I built an inventory app where you can add items and store them in a database, so the list
                can be updated, tracked, and improved over time. The goal is to keep it practical for a busy
                environment: quick to use, clear layout, no unnecessary steps.
              </p>

              <div className="pp-snapshot">
                <div className="pp-snapshot-item">
                  <span className="pp-label">Project Type</span>
                  <span className="pp-value">Personal / Tech Case (Smart Mobile)</span>
                </div>

                <div className="pp-snapshot-item">
                  <span className="pp-label">Goal</span>
                  <span className="pp-value">Track stock and reduce daily guesswork</span>
                </div>

                <div className="pp-snapshot-item">
                  <span className="pp-label">Users</span>
                  <span className="pp-value">Small restaurant team / family business</span>
                </div>

                <div className="pp-snapshot-item">
                  <span className="pp-label">Focus</span>
                  <span className="pp-value">Simple UI, clear data structure, practical workflow</span>
                </div>

                <div className="pp-snapshot-item">
                  <span className="pp-label">Tools</span>
                  <span className="pp-value">SwiftUI, Firebase (Firestore), Git</span>
                </div>
              </div>

            </section>

            <section id="features" className="pp-section">
              <h2>Features</h2>

              <h3>What’s working now</h3>
              <ul>
                <li>Add inventory items from the app</li>
                <li>Save items into a database (not local-only)</li>
                <li>Structured item fields (name, category, quantity, notes)</li>
              </ul>

              <h3>Next features I want to build</h3>
              <ul>
                <li>
                  <b>Stock updates:</b> stock in / stock out (so daily usage gets tracked)
                </li>
                <li>
                  <b>Low-stock detection:</b> highlight items that are about to run out
                </li>
                <li>
                  <b>Reorder list:</b> automatically generate a list of what needs restocking
                </li>
                <li>
                  <b>Usage tracking:</b> see what items are used most per day/week
                </li>
                <li>
                  <b>Notifications (optional):</b> reminders when an item drops below a threshold
                </li>
              </ul>

              <p>
                Basically, I want to push this from “inventory list” into an “inventory system” that’s actually
                useful during real restaurant operations.
              </p>
            </section>

            <section id="learning" className="pp-section">
              <h2>What I Learned</h2>
              <p>
                Building this taught me a lot about making apps that don’t just “work”, but actually fit real-life use.
                The database part especially made me think ahead, because once the structure is solid, features become
                easier to build.
              </p>
              <ul>
                <li>How data structure affects future features (reporting, restock logic, tracking)</li>
                <li>Designing a simple workflow for busy users</li>
                <li>Planning architecture step-by-step (MVP first, then improving)</li>
              </ul>
              <p>
                Most importantly, it reminded me why I like building products when it solves a real problem, it feels
                way more meaningful than a random practice project.
              </p>
            </section>
          </div>

          {/* RIGHT: Slideshow */}
          <aside className="pp-right">
            {images.length > 0 ? (
              <Slideshow images={images} />
            ) : (
              <div className="slideshow-placeholder">
                <p>Slideshow Placeholder</p>
                <p>Add images to the images array to display slideshow</p>
              </div>
            )}

            <div className="pp-caption">
              <p>
               
              </p>
            </div>
          </aside>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default InventoryManagementApp;
